import React from "react";

// Mui
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

// Mui Styles
import { StyledPaper } from "@/styles/mui/globalStyles";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import EditIcon from "@mui/icons-material/Edit";
import LayersIcon from "@mui/icons-material/Layers";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { createTheme, ThemeProvider } from "@mui/material/styles";
// Navigate
import { useNavigate, useParams } from "react-router-dom";

// Components
import Layout from "@/components/layout";
import Title from "@/components/title";

// Fetch
import {
  cloneLanding,
  createOrEditLandingPage,
  deleteImages,
  deleteLandingImagesById,
  floatLandingImagesById,
  getLandingDetailsById,
  hiddenLandingImagesById,
  insertLandingImagesById,
  landingReorder,
  uploadImages,
} from "@/fetch/fetchOffice";

// Config
import { uuid } from "@/config/normalConfig";

// Other
import { Buffer } from "buffer";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDropzone } from "react-dropzone";

import connectConfig from "@/config/connectConfig";

const LandingSettingDetailPage = (props) => {
  const { open, setOpen } = props;
  const { id } = useParams();
  const canvasRef = React.useRef(null);
  // Navigate
  const navigate = useNavigate();

  // States
  const [onChangeData, setOnChangeData] = React.useState({});
  const [slideTranOn, setSlideTranOn] = React.useState(false);
  const [snackMessage, setSnackMessage] = React.useState("");
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [files, setFiles] = React.useState([]);
  const [filesPreview, setFilesPreview] = React.useState([]);
  const [deleteFileDetail, setDeleteFileDetail] = React.useState([]);
  const [lock, setLock] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);

  // DropZone
  const { getRootProps, getInputProps } = useDropzone({
    disabled: lock,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/gif": [".gif"],
      "image/webp": [".webp"],
    },
    onDrop: (acceptedFiles) => {
      let tempArr = [],
        tempObj = {},
        count = 0;

      if (id) {
        tempArr.push(...filesPreview);
      }

      for (const file of acceptedFiles) {
        tempObj = {
          title: file.name,
          img: URL.createObjectURL(file),
          flag: "new",
        };

        tempArr.push(tempObj);
        // eslint-disable-next-line
        count++;
      }

      setFilesPreview(tempArr);
      setFiles(acceptedFiles);
    },
  });

  // Handle Delete Cliked
  const handleClickDelete = (item, action) => {
    let tempArr = [...deleteFileDetail];
    if (action === "markAsDelete") {
      tempArr.push({
        id: item.id,
        key: item.images_name,
      });

      setDeleteFileDetail(tempArr);
    }
    if (action === "cancel") {
      // Find the files object with the matching ID
      const objectToDelete = tempArr.find((obj) => obj.id === item.id);

      if (objectToDelete) {
        tempArr.splice(tempArr.indexOf(objectToDelete), 1);
        setDeleteFileDetail(tempArr);
      }
    }
  };

  // Delete Clicked
  const comfirmDelete = async () => {
    setLock(true);
    let tempArr = [...deleteFileDetail];
    try {
      for (let i = 0; i < tempArr.length; i++) {
        const params = {
          Bucket: "3cb-public-images",
          Key: tempArr[i].key,
        };
        const data = await deleteImages(params);

        if (data.results) {
          await deleteLandingImagesById(id, tempArr[i].id);
        }
      }

      setDeleteFileDetail([]);

      setLock(false);
      setOpenDialog(false);

      setSnackMessage("刪除成功");
      setSnackOpen(true);
      setTimeout(() => {
        setSnackOpen(false);
      }, 1500);

      fetchAll();
    } catch (err) {
      console.log("Error", err);
    }
  };

  // handleCancelFile
  const handleCancelFile = (item) => {
    let filesPreviewArr = [...filesPreview];
    let filesArr = files;

    // Find the files preview object with the matching ID
    const objectToDelete = filesPreviewArr.find(
      (obj) => obj.title === item.title
    );
    // Find the files object with the matching ID
    const objectToDelete2 = filesArr.find((obj) => obj.name === item.title);

    if (objectToDelete) {
      filesPreviewArr.splice(filesPreviewArr.indexOf(objectToDelete), 1);
      setFilesPreview(filesPreviewArr);
    }
    if (objectToDelete2) {
      filesArr.splice(filesArr.indexOf(objectToDelete2), 1);
      setFiles(filesArr);
    }
  };

  // handleClickHidden
  const handleClickHidden = async (item) => {
    await hiddenLandingImagesById(item.id, !item.images_hidden).then(() => {
      fetchAll();
    });
  };

  // handleClickFloat
  const handleClickFloat = async (item) => {
    await floatLandingImagesById(item.id, !item.images_floating).then(() => {
      fetchAll();
    });
  };

  // Upload to AWS S3
  const uploadToS3 = async (key, buffer, landing_master_id, types) => {
    const params = {
      Bucket: "3cb-public-images",
      Key: key,
      Body: buffer,
      ContentEncoding: "base64",
      ContentType: `image/${types}`,
      ContentDisposition: "inline",
      ACL: "public-read",
    };
    const data = await uploadImages(params);

    if (data.results) {
      await insertLandingImagesById(landing_master_id, data.data).then(() => {
        setSnackMessage("上傳成功");
        setSnackOpen(true);
        setTimeout(() => {
          setSnackOpen(false);
          fetchAll();
        }, 1500);

        setFiles([]);
        setLock(false);
        if (id === undefined) {
          navigate("/landing");
        }
      });
    }
  };

  // Change files format
  const handleImages = async (landing_master_id) => {
    let tempFilePreview = [...filesPreview];
    let dotTypes;
    if (onChangeData.brand !== "") {
      for (const file of files) {
        if (file.type !== "image/gif" && file.type !== "image/webp") {
          dotTypes = ".webp";

          const image = new Image();
          image.src = URL.createObjectURL(file);
          await new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = reject;
          });
          canvasRef.current.width = image.width;
          canvasRef.current.height = image.height;
          const context = canvasRef.current.getContext("2d");
          context.drawImage(image, 0, 0);
          const dataURL = canvasRef.current.toDataURL("image/webp", 0.75);
          const base64Data = dataURL.replace(/^data:image\/webp;base64,/, "");
          const buffer = Buffer.from(base64Data, "base64");

          await uploadToS3(
            `landing/${onChangeData.brand}/${uuid()}.webp`,
            buffer,
            landing_master_id,
            "webp"
          );
        } else if (file.type === "image/gif") {
          dotTypes = ".gif";
          const reader = new FileReader();

          reader.onloadend = async () => {
            const result = reader.result;
            const base64Data = result.replace(/^data:image\/gif;base64,/, "");
            const buffer = Buffer.from(base64Data, "base64");

            await uploadToS3(
              `landing/${onChangeData.brand}/${uuid()}.gif`,
              buffer,
              landing_master_id,
              "gif"
            );
          };

          reader.readAsDataURL(file);
        } else if (file.type === "image/webp") {
          dotTypes = ".webp";
          const reader = new FileReader();

          reader.onloadend = async () => {
            const result = reader.result;
            const base64Data = result.replace(/^data:image\/webp;base64,/, "");
            const buffer = Buffer.from(base64Data, "base64");

            await uploadToS3(
              `landing/${onChangeData.brand}/${uuid()}.gif`,
              buffer,
              landing_master_id,
              "webp"
            );
          };
          reader.readAsDataURL(file);
        }

        const title = file.name;
        const arrayIndex = tempFilePreview.findIndex(
          (item) => item.title === title
        );
        tempFilePreview[arrayIndex] = {
          ...tempFilePreview,
          images_name: `landing/${onChangeData.brand}/${uuid()}${dotTypes}`,
        };

        await landingReorder(landing_master_id, tempFilePreview).then(() => {
          fetchAll();
        });
      }
    }
  };

  // Submit
  const onSaveChange = async () => {
    let uploadData = false;
    let deleteData = false;
    if (
      onChangeData.brand !== "" &&
      onChangeData.product !== "" &&
      onChangeData.source !== ""
      // onChangeData.bitly3chembio !== "" &&
      // onChangeData.bitlymannings !== ""
    ) {
      setLock(true);
      await createOrEditLandingPage(id, onChangeData).then(async (data) => {
        if (data && data.results) {
          setSnackMessage(data.message);

          if (files.length > 0) {
            uploadData = true;
            handleImages(data.id);
          } else {
            setLock(false);
          }
          if (id) {
            if (deleteFileDetail.length > 0) {
              deleteData = true;
              comfirmDelete();
            }
            if (deleteData === false && uploadData === false) {
              await landingReorder(id, filesPreview);
            }
          }
        }

        setSnackOpen(true);
        setTimeout(() => {
          setSnackOpen(false);
        }, 1500);

        if (id === undefined && uploadData === false) {
          setTimeout(() => {
            navigate("/landing");
          }, 1000);
        }
      });
    } else {
      setSnackMessage("請輸入全部資料");
      setLock(false);
      setSnackOpen(true);
      setTimeout(() => {
        setSnackOpen(false);
      }, 1500);
    }
  };
  const fetchAll = async () => {
    await getLandingDetailsById(id).then((data) => {
      if (data && data.results) {
        setSlideTranOn(true);
        setOnChangeData({
          title: data.info[0].title,
          brand: data.info[0].brand,
          product: data.info[0].product,
          source: data.info[0].source,
          status: data.info[0].status,
          bitly3chembio: data.info[0].bitly3chembio,
          bitlymannings: data.info[0].bitlymannings,
          bitlyfloating: data.info[0].bitlyfloating,
        });
        let tempArr = [...data.images];
        for (let i = 0; i < tempArr.length; i++) {
          tempArr[i].title = tempArr[i].images_name;
          tempArr[
            i
          ].img = `${connectConfig.s3Landing}/${tempArr[i].images_name}`;
          tempArr[i].flag = "exist";
        }
        setFilesPreview(tempArr);
      }
    });
  };

  const theme = createTheme({
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            marginBottom: 10,
          },
        },
      },
    },
  });

  React.useEffect(() => {
    if (id) {
      fetchAll();
    } else {
      setOnChangeData({
        title: "",
        brand: "",
        product: "",
        source: "",
        status: "",
        bitly3chembio: "",
        bitlymannings: "",
        bitlyfloating: "",
      });
      setSlideTranOn(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(filesPreview);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFilesPreview(items);
  };
  return (
    <Layout
      header="LandingSettingDetailPage"
      selectedIndex={13}
      snackOpen={snackOpen}
      snackClose={() => setSnackOpen(false)}
      snackMessage={snackMessage}
      open={open}
      setOpen={setOpen}
    >
      <Title
        title={`廣告頁 / ${id ? "修改廣告頁" : "新增廣告頁"}`}
        history={() => navigate(-1)}
        children={
          <div>
            {id && (
              <IconButton
                component="label"
                onClick={() => setEditMode(!editMode)}
                disabled={lock}
                color={editMode ? "info" : "default"}
              >
                <EditIcon />
              </IconButton>
            )}
            {editMode ? (
              <IconButton
                component="label"
                onClick={() => setOpenDialog(true)}
                disabled={deleteFileDetail.length <= 0 ? true : false}
              >
                <DeleteSweepIcon />
              </IconButton>
            ) : (
              <>
                {id && (
                  <IconButton
                    component="label"
                    onClick={() =>
                      window.open(
                        `https://landing.threechembio.com/${onChangeData.brand}-${onChangeData.product}-${onChangeData.source}`
                      )
                    }
                  >
                    <OpenInNewIcon />
                  </IconButton>
                )}

                <IconButton
                  component="label"
                  onClick={() => onSaveChange()}
                  disabled={lock}
                >
                  <SaveIcon />
                </IconButton>
              </>
            )}
            {id && (
              <IconButton
                component="label"
                onClick={async () => {
                  try {
                    const confirm = window.confirm("你要複製此廣告頁嗎?");
                    if (confirm) {
                      const { data } = await cloneLanding(id);
                      setSnackMessage("複製成功");
                      setSnackOpen(true);
                      navigate(`/landing/edit/${data}`);
                      navigate(0);
                    }
                  } catch {
                    setSnackMessage("複製失敗");
                    setSnackOpen(true);
                  }
                }}
                disabled={lock}
                color={editMode ? "info" : "default"}
              >
                <ContentCopyIcon />
              </IconButton>
            )}
          </div>
        }
      />
      <Fade in={slideTranOn} mountOnEnter unmountOnExit>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 12,
              lg: 12
            }}>
            <StyledPaper page={"yes"}>
              <ThemeProvider theme={theme}>
                <Grid container spacing={2}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 3
                    }}>
                    <TextField
                      id="outlined-name"
                      name="title"
                      label="標題"
                      error={onChangeData.title ? false : true}
                      value={onChangeData.title}
                      onChange={(event) =>
                        setOnChangeData({
                          ...onChangeData,
                          [event.target.name]: event.target.value,
                        })
                      }
                      sx={{ width: "100%" }}
                      disabled={lock}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 3
                    }}>
                    <TextField
                      id="outlined-name"
                      name="brand"
                      label="品牌"
                      error={onChangeData.brand ? false : true}
                      value={onChangeData.brand}
                      onChange={(event) =>
                        setOnChangeData({
                          ...onChangeData,
                          [event.target.name]: event.target.value,
                        })
                      }
                      sx={{ width: "100%" }}
                      disabled={id ? true : false}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 3
                    }}>
                    <TextField
                      id="outlined-name"
                      name="product"
                      label="產品"
                      error={onChangeData.product ? false : true}
                      value={onChangeData.product}
                      onChange={(event) =>
                        setOnChangeData({
                          ...onChangeData,
                          [event.target.name]: event.target.value,
                        })
                      }
                      sx={{ width: "100%" }}
                      disabled={lock}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 3
                    }}>
                    <TextField
                      id="outlined-name"
                      name="source"
                      label="來源"
                      error={onChangeData.source ? false : true}
                      value={onChangeData.source}
                      onChange={(event) =>
                        setOnChangeData({
                          ...onChangeData,
                          [event.target.name]: event.target.value,
                        })
                      }
                      sx={{ width: "100%" }}
                      disabled={lock}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 3
                    }}>
                    <TextField
                      id="outlined-name"
                      name="bitly3chembio"
                      label="3CB連結"
                      error={onChangeData.bitly3chembio ? false : true}
                      value={onChangeData.bitly3chembio}
                      onChange={(event) =>
                        setOnChangeData({
                          ...onChangeData,
                          [event.target.name]: event.target.value,
                        })
                      }
                      sx={{ width: "100%" }}
                      disabled={lock}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 3
                    }}>
                    <TextField
                      id="outlined-name"
                      name="bitlymannings"
                      label="萬寧連結"
                      error={onChangeData.bitlymannings ? false : true}
                      value={onChangeData.bitlymannings}
                      onChange={(event) =>
                        setOnChangeData({
                          ...onChangeData,
                          [event.target.name]: event.target.value,
                        })
                      }
                      sx={{ width: "100%" }}
                      disabled={lock}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 3
                    }}>
                    <TextField
                      id="outlined-name"
                      name="bitlyfloating"
                      label="浮動按扭連結"
                      error={onChangeData.bitlyfloating ? false : true}
                      value={onChangeData.bitlyfloating}
                      onChange={(event) =>
                        setOnChangeData({
                          ...onChangeData,
                          [event.target.name]: event.target.value,
                        })
                      }
                      sx={{ width: "100%" }}
                      disabled={lock}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 3
                    }}>
                    <TextField
                      id="outlined-select"
                      select
                      label="選擇狀態"
                      name="status"
                      value={onChangeData.status}
                      onChange={(event) =>
                        setOnChangeData({
                          ...onChangeData,
                          [event.target.name]: event.target.value,
                        })
                      }
                      sx={{ width: "100%" }}
                      disabled={lock}
                    >
                      <MenuItem value={""}>請選擇</MenuItem>
                      <MenuItem value={false}>草稿</MenuItem>
                      <MenuItem value={true}>發佈</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </ThemeProvider>
            </StyledPaper>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 12,
              lg: 12
            }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <ThemeProvider theme={theme}>
                <StyledPaper page={"yes"}>
                  <Box
                    noValidate
                    sx={{
                      display: "flex",
                      width: "100%",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px dashed",
                      borderRadius: "20px",
                      borderColor: lock ? "#dbdbdb" : "black",
                      marginBottom: "20px",
                    }}
                    {...getRootProps()}
                  >
                    <input {...getInputProps()} />
                    <p>
                      {lock ? (
                        <>
                          上傳中 <CircularProgress size={10} />
                        </>
                      ) : files.length > 0 ? (
                        `已選擇${files.length}張圖片`
                      ) : (
                        "按此 / 拉進 選擇圖片"
                      )}
                    </p>
                    <canvas ref={canvasRef} style={{ display: "none" }} />
                  </Box>
                  {filesPreview.length > 0 && (
                    <Droppable droppableId="imagesList" direction="horizontal">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            display: "flex",
                            overflowX: "scroll",
                            overflowY: "hidden",
                            width: "100%",
                          }}
                        >
                          {filesPreview.map((item, i) => (
                            <Draggable
                              key={item.title}
                              draggableId={item.title.toString()}
                              index={i}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-around",
                                    }}
                                  >
                                    {editMode && (
                                      <>
                                        {item.flag === "exist" ? (
                                          <>
                                            <div>
                                              <IconButton
                                                component="label"
                                                onClick={() =>
                                                  handleClickHidden(item)
                                                }
                                                color={
                                                  item.images_hidden
                                                    ? "error"
                                                    : "default"
                                                }
                                              >
                                                {item.images_hidden ? (
                                                  <VisibilityOffIcon />
                                                ) : (
                                                  <VisibilityIcon />
                                                )}
                                              </IconButton>
                                              <IconButton
                                                component="label"
                                                onClick={() =>
                                                  handleClickFloat(item)
                                                }
                                                color={
                                                  item.images_floating
                                                    ? "error"
                                                    : "default"
                                                }
                                              >
                                                <LayersIcon />
                                              </IconButton>
                                            </div>
                                            <IconButton
                                              component="label"
                                              color={
                                                deleteFileDetail.some(
                                                  (value) =>
                                                    value.id === item.id
                                                )
                                                  ? "error"
                                                  : "default"
                                              }
                                              onClick={() =>
                                                handleClickDelete(
                                                  item,
                                                  deleteFileDetail.some(
                                                    (value) =>
                                                      value.id === item.id
                                                  )
                                                    ? "cancel"
                                                    : "markAsDelete"
                                                )
                                              }
                                              disabled={lock}
                                            >
                                              {deleteFileDetail.some(
                                                (value) => value.id === item.id
                                              ) ? (
                                                <DeleteForeverIcon />
                                              ) : (
                                                <DeleteIcon />
                                              )}
                                            </IconButton>
                                          </>
                                        ) : (
                                          <IconButton
                                            component="label"
                                            color="info"
                                            onClick={() =>
                                              handleCancelFile(item)
                                            }
                                            disabled={lock}
                                          >
                                            <CancelIcon />
                                          </IconButton>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <img
                                    src={item.img}
                                    alt={item.title}
                                    loading="lazy"
                                    style={{
                                      maxWidth: 250,
                                      minWidth: 250,
                                      borderRadius: 20,
                                      marginRight: 10,
                                      opacity: item.images_hidden ? 0.3 : 1,
                                    }}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}

                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  )}
                </StyledPaper>
              </ThemeProvider>
            </DragDropContext>
          </Grid>
        </Grid>
      </Fade>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">確認要刪除圖片嗎？</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            一旦刪除沒有辦法 無法返回動作
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {lock ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                padding: "10px 0px",
              }}
            >
              <CircularProgress size={30} />
            </div>
          ) : (
            <>
              <Button onClick={() => setOpenDialog(false)}>取消</Button>
              <Button onClick={() => comfirmDelete()}>確認刪除圖片</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default LandingSettingDetailPage;
